import {
  getTryptiqueMessage,
  getTryptiqueRawXml,
  listTryptiqueMessages,
  type TryptiqueDetail,
  type TryptiqueListItem,
  type TryptiqueListQuery,
} from '../api/tryptique';
import { StatusBadge } from '../components/StatusBadge';
import { CorrelationIdField, CustomsMessageConsole } from '../components/CustomsMessageConsole';
import { simpleValidationSections } from '../lib/customsMessages';

const PAGE_SIZE = 20;

const initialFilters: TryptiqueListQuery = {
  page: 1,
  pageSize: PAGE_SIZE,
  referenceTryptique: '',
  partnerCode: '',
  fonctionMessage: '',
  processingStatus: '',
};

export function TryptiquePage() {
  return (
    <CustomsMessageConsole<TryptiqueListItem, TryptiqueListQuery, TryptiqueDetail>
      eyebrow="Maritime"
      title="Tryptique"
      purpose="Inspect ADII Tryptique XML messages (import D17 / export D20), their normalized JSON, validation results and processing history. Read-only in this release; reprocess/replay actions are a planned follow-up."
      status="PARTIAL"
      next="Contact platform engineering to reprocess or replay a message until dedicated operator actions ship."
      listPanelTitle="Tryptique messages"
      primaryColumnHeader="Reference Tryptique"
      emptyRowLabel="No Tryptique message matches the current filters."
      detailTitle="Tryptique detail"
      initialFilters={initialFilters}
      pageSize={PAGE_SIZE}
      listFetch={listTryptiqueMessages}
      detailFetch={getTryptiqueMessage}
      rawXmlFetch={getTryptiqueRawXml}
      getRowId={(item) => item.id}
      getRowLabel={(item) => item.referenceTryptique ?? item.id}
      filterFields={(filters, patchFilters) => (
        <>
          <label className="filter-field">
            <span>Reference Tryptique</span>
            <input value={filters.referenceTryptique ?? ''} onChange={(event) => patchFilters({ referenceTryptique: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Partner</span>
            <input value={filters.partnerCode ?? ''} onChange={(event) => patchFilters({ partnerCode: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Flow</span>
            <select value={filters.flow ?? ''} onChange={(event) => patchFilters({ flow: event.target.value as TryptiqueListQuery['flow'] })}>
              <option value="">All</option>
              <option value="IMPORT_D17">Import D17</option>
              <option value="EXPORT_D20">Export D20</option>
              <option value="UNKNOWN">Unknown</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Régime</span>
            <select value={filters.regime ?? ''} onChange={(event) => patchFilters({ regime: event.target.value as TryptiqueListQuery['regime'] })}>
              <option value="">All</option>
              <option value="009">009</option>
              <option value="900">900</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Processing status</span>
            <select value={filters.processingStatus ?? ''} onChange={(event) => patchFilters({ processingStatus: event.target.value })}>
              <option value="">All</option>
              <option value="PROCESSED">Processed</option>
              <option value="REPLAYED">Replayed</option>
              <option value="REJECTED">Rejected</option>
              <option value="QUARANTINED">Quarantined</option>
              <option value="FAILED">Failed</option>
            </select>
          </label>
        </>
      )}
      listColumns={[
        { header: 'Flow', render: (item) => item.flow },
        { header: 'Régime', render: (item) => item.regime },
        { header: 'Fonction', render: (item) => item.fonctionMessageLabel },
        { header: 'Partner', render: (item) => item.partnerCode ?? 'n/a' },
        { header: 'Validation', render: (item) => <StatusBadge value={item.validationStatus} /> },
        { header: 'Processing', render: (item) => <StatusBadge value={item.processingStatus} /> },
        { header: 'Received', render: (item) => new Date(item.createdAt).toLocaleString('en-GB') },
      ]}
      detailSummaryFields={(data) => (
        <>
          <div><strong>Flow</strong><span>{data.flow}</span></div>
          <div><strong>Régime</strong><span>{data.regime}</span></div>
          <div><strong>Fonction message</strong><span>{data.fonctionMessageLabel}</span></div>
          <div><strong>Lifecycle event</strong><span>{data.lifecycleEventLabel ?? 'n/a'}</span></div>
          <div><strong>Root element</strong><span>{data.rootElement}</span></div>
          <div><strong>Filename</strong><span className="mono">{data.filename ?? 'n/a'}</span></div>
          <div><strong>Partner</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>Reference Tryptique</strong><span className="mono">{data.referenceTryptique ?? 'n/a'}</span></div>
          <div><strong>Date enregistrement</strong><span>{data.dateEnregistrementRaw ?? 'n/a'}</span></div>
          <div><strong>Date mainlevée</strong><span>{data.dateMainleveeRaw ?? 'n/a'}</span></div>
          <div><strong>Validation</strong><span><StatusBadge value={data.validationStatus} /></span></div>
          <div><strong>Processing</strong><span><StatusBadge value={data.processingStatus} /></span></div>
          <CorrelationIdField value={data.correlationId} />
        </>
      )}
      detailValidationReport={(data) => simpleValidationSections(data.validationReport)}
    />
  );
}
